import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import StationCard from "../components/StationCard";
import NowPlayingHistory from "../components/NowPlayingHistory";
import OnAirNowBanner from "../components/OnAirNowBanner";
import SponsorRotator from "../components/SponsorRotator";
import HeroVideoBackground from "../components/HeroVideoBackground";
import NewsTicker from "../components/NewsTicker";
import { STATIONS, HERO_BG } from "../config/stations";
import { useChat } from "../context/ChatContext";
import { usePlayer } from "../context/PlayerContext";
import {
  getHomeStation,
  wasHomeToastShown,
  markHomeToastShown,
  isReturningListener,
} from "../lib/favorite-station";
import { Radio, Sparkles, Download, Home } from "lucide-react";

const Landing = () => {
  const { setDrawerOpen } = useChat();
  const { current, play } = usePlayer();

  // Home-station tune-in (once per session, only if no station is already playing).
  // Returning fans get silent autoplay; first-time visitors see a tap-to-play toast.
  useEffect(() => {
    if (current) return;
    if (wasHomeToastShown()) return;
    const homeId = getHomeStation();
    if (!homeId) return;
    const station = STATIONS.find((s) => s.id === homeId);
    if (!station) return;

    const t = setTimeout(async () => {
      markHomeToastShown();
      if (isReturningListener()) {
        // Try silent autoplay for a returning fan
        try {
          await play(station.id);
          return;
        } catch {
          /* fall through to toast */
        }
      }
      // First-time visitor (or autoplay refused) → tap-to-play toast
      toast(`${station.name} is your home station`, {
        description: "Tap play to tune straight in.",
        duration: 15000,
        icon: <Home className="w-4 h-4 text-gold" />,
        action: {
          label: "Play",
          onClick: () => play(station.id),
        },
      });
    }, 800);
    return () => clearTimeout(t);
  }, [current, play]);

  return (
    <div>
      {/* On-Air Now / Up Next banner */}
      <OnAirNowBanner />

      {/* Scrolling headline ticker (only renders if there are published articles) */}
      <NewsTicker />

      {/* Hero — Official GFN Banner (logos are clickable via overlay hotspots + jump strip below) */}
      <section className="relative overflow-hidden">
        <HeroVideoBackground />
        <div className="relative z-[1] max-w-7xl mx-auto px-5 md:px-10 pt-6 md:pt-10 pb-10 md:pb-16">
          <div className="relative rounded-lg overflow-hidden border border-gold/30 shadow-[0_0_60px_rgba(212,175,55,0.12)]">
            <img
              src={HERO_BG}
              alt="GFN Soul NYC — One Network, One Family, One Mission"
              className="w-full h-auto block"
              data-testid="hero-banner"
            />
            {/* Clickable logo hotspots — the 4 station logos sit as a horizontal row in the
                bottom half of the banner. Positions are percentages so they scale responsively. */}
            <Link
              to="/fm151"
              data-testid="hero-hotspot-fm151"
              aria-label="Open FM 151"
              className="absolute top-[50%] left-[3%] w-[22%] h-[45%] rounded-lg ring-0 hover:ring-2 hover:ring-gold/70 hover:bg-gold/5 transition-all"
            />
            <Link
              to="/vibe"
              data-testid="hero-hotspot-vibe"
              aria-label="Open The Vibe"
              className="absolute top-[50%] left-[26%] w-[22%] h-[45%] rounded-lg ring-0 hover:ring-2 hover:ring-gold/70 hover:bg-gold/5 transition-all"
            />
            <Link
              to="/gfn-tv"
              data-testid="hero-hotspot-tv"
              aria-label="Open GFN Soul NYC TV"
              className="absolute top-[50%] left-[49%] w-[22%] h-[45%] rounded-lg ring-0 hover:ring-2 hover:ring-gold/70 hover:bg-gold/5 transition-all"
            />
            <Link
              to="/on-demand"
              data-testid="hero-hotspot-on-demand"
              aria-label="Open GFN Soul On Demand"
              className="absolute top-[50%] left-[72%] w-[22%] h-[45%] rounded-lg ring-0 hover:ring-2 hover:ring-gold/70 hover:bg-gold/5 transition-all"
            />
          </div>

          {/* Visible station-jump strip — always tappable */}
          <div
            data-testid="hero-jump-strip"
            className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2"
          >
            <Link
              to="/fm151"
              data-testid="hero-jump-fm151"
              className="flex items-center justify-center gap-2 border border-gold/30 hover:border-gold hover:bg-gold/10 rounded-full px-4 py-3 font-condensed uppercase tracking-widest text-[11px] text-gold transition-colors"
            >
              FM 151
            </Link>
            <Link
              to="/vibe"
              data-testid="hero-jump-vibe"
              className="flex items-center justify-center gap-2 border border-gold/30 hover:border-gold hover:bg-gold/10 rounded-full px-4 py-3 font-condensed uppercase tracking-widest text-[11px] text-gold transition-colors"
            >
              The Vibe
            </Link>
            <Link
              to="/gfn-tv"
              data-testid="hero-jump-tv"
              className="flex items-center justify-center gap-2 border border-gold/30 hover:border-gold hover:bg-gold/10 rounded-full px-4 py-3 font-condensed uppercase tracking-widest text-[11px] text-gold transition-colors"
            >
              GFN Soul NYC TV
            </Link>
            <Link
              to="/on-demand"
              data-testid="hero-jump-on-demand"
              className="flex items-center justify-center gap-2 border border-gold/30 hover:border-gold hover:bg-gold/10 rounded-full px-4 py-3 font-condensed uppercase tracking-widest text-[11px] text-gold transition-colors"
            >
              GFN Soul On Demand
            </Link>
          </div>

          <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
                <span className="relative flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-crimson-live live-dot" />
                </span>
                <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
                  On Air · 24 / 7 / 365
                </span>
              </div>
              <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] text-white uppercase">
                One Network. <span className="text-gold-gradient">One Family.</span>{" "}
                <span className="text-crimson">One Mission.</span>
              </h1>
              <p className="mt-4 max-w-2xl font-body text-neutral-300 text-base md:text-lg">
                Two radio stations, one live TV network, and a full on-demand catalog —
                built for real soul, real talk, real community.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#stations"
                data-testid="hero-tune-in"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
              >
                <Radio className="w-4 h-4" />
                Pick a Station
              </a>
              <Link
                to="/install"
                data-testid="hero-install-app"
                className="relative inline-flex items-center gap-2 bg-gradient-to-r from-gold-dark via-gold to-gold-dark hover:from-gold hover:to-gold text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-all shadow-[0_0_24px_rgba(212,175,55,0.35)]"
              >
                <Download className="w-4 h-4" />
                Install GFN App
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/80 text-gold text-[9px] tracking-widest">
                  FREE
                </span>
              </Link>
              <button
                data-testid="hero-chat"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Request a Song
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stations */}
      <section id="stations" className="relative max-w-7xl mx-auto px-5 md:px-10 pt-4">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-condensed uppercase tracking-[0.35em] text-[11px] text-gold">
              Choose Your Frequency
            </div>
            <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase text-white">
              Three ways to <span className="text-gold-gradient">tune in</span>
            </h2>
          </div>
          {current && (
            <div className="hidden md:block text-right">
              <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500">
                Now streaming
              </div>
              <div className="font-condensed uppercase text-sm text-gold">{current.name}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {STATIONS.map((s) => (
            <StationCard key={s.id} station={s} />
          ))}
        </div>
      </section>

      {/* Recently Played history for each audio station */}
      <NowPlayingHistory />

      {/* Sponsors marquee (managed from /admin) */}
      <SponsorRotator />
    </div>
  );
};

export default Landing;
